import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { del, get, post, put, type ApiPath } from '@app/api/typed';
import { identitiesKeys } from '../Identities/Identities.api';

/** `{id}` do contrato → UUID, sem perder o tipo do path (mesmo helper de Users.api). */
function rota<P extends ApiPath>(template: P, id: string): P {
  return template.replace('{id}', encodeURIComponent(id)) as P;
}
import type {
  DiscoveryConfig,
  DiscoveryConfigUpdate,
  Environment,
  EnvironmentInput,
  EnvironmentList,
  IdentityRole,
  IdentityRoleUpdate,
  TenantConfig,
  TenantUpdate,
} from './TenantSettingsModel';

export const tenantKeys = {
  tenant: ['tenant'] as const,
  discovery: ['tenant', 'discovery'] as const,
  environments: ['tenant', 'environments'] as const,
};

/** Configuração do tenant (T21). Segredos só chegam como `*_set` (FR-020). */
export function useTenant(): UseQueryResult<TenantConfig, ApiError> {
  return useQuery<TenantConfig, ApiError>({
    queryKey: tenantKeys.tenant,
    queryFn: () => get('/api/v1/tenant'),
  });
}

/**
 * Salvar o tenant (FR-016). A resposta é a configuração efetiva já recarregada —
 * semeamos o cache com ela para a tela mostrar o que **ficou gravado**, não o que
 * foi enviado (FR-022).
 *
 * Depois de salvar, o `getTenant` é relido: o `trust_domain_middleware` da
 * resposta do PUT **não** consulta o middleware (é o que ficou gravado), e o
 * aviso de divergência precisa da leitura ao vivo (FR-115).
 */
export function useUpdateTenant(): UseMutationResult<TenantConfig, ApiError, TenantUpdate> {
  const queryClient = useQueryClient();
  return useMutation<TenantConfig, ApiError, TenantUpdate>({
    mutationFn: (body) => put('/api/v1/tenant', { body }),
    onSuccess: (tenant) => {
      queryClient.setQueryData(tenantKeys.tenant, tenant);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.tenant });
      // O chip do executor e «Cliente · ambiente» na topbar saem do mesmo estado
      // (FR-015, FR-119).
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
      void queryClient.invalidateQueries({ queryKey: shellKeys.me });
    },
    onError: (error) => {
      // 409 `versao_antiga`: alguém salvou antes — recarrega para o próximo PUT
      // sair com a `version` certa (FR-124).
      if (error.code === 'versao_antiga') {
        void queryClient.invalidateQueries({ queryKey: tenantKeys.tenant });
      }
    },
  });
}

/**
 * O `useTestMiddleware` SAIU NO S-e (2), com a rota `testMiddleware`.
 *
 * Ele já era um hook sem chamador: o card «Middleware» de T21 saiu no S-a, quando a
 * ponte virou do SITE. O botão que faz esta pergunta hoje é o «Testar conexão» do
 * drawer de «Sites» (`useTestSiteConnection`, S-b (b)), que sonda o executor DAQUELE
 * cluster e devolve três respostas em vez de uma — sondagem, leitura do `GET /config`
 * e a conferência do trust domain.
 *
 * Hook sem chamador não fica: ele é o modelo que a próxima pessoa copia, e copiar
 * este significaria voltar a perguntar «o middleware está de pé?» sem dizer qual.
 */

// ---------------------------------------------------------------- discovery (T426)

/** Estado do discovery: ativo/pausado, intervalo, última varredura (FR-117). */
export function useDiscoveryConfig(): UseQueryResult<DiscoveryConfig, ApiError> {
  return useQuery<DiscoveryConfig, ApiError>({
    queryKey: tenantKeys.discovery,
    queryFn: () => get('/api/v1/tenant/discovery'),
  });
}

/** Ativar/pausar e definir o intervalo (≥ 1 min). Local, sem middleware. */
export function useUpdateDiscoveryConfig(): UseMutationResult<
  DiscoveryConfig,
  ApiError,
  DiscoveryConfigUpdate
> {
  const queryClient = useQueryClient();
  return useMutation<DiscoveryConfig, ApiError, DiscoveryConfigUpdate>({
    mutationFn: (body) => put('/api/v1/tenant/discovery', { body }),
    onSuccess: (config) => {
      queryClient.setQueryData(tenantKeys.discovery, config);
      // O documento do tenant mudou de `version` (o discovery grava pela mesma
      // linha) — o card Tenant precisa da versão nova para o próximo PUT.
      void queryClient.invalidateQueries({ queryKey: tenantKeys.tenant });
    },
  });
}

// ---------------------------------------------------------------- papéis (T427)

/** Editar a descrição do papel (FR-118) — o nome não muda. */
export function useUpdateIdentityRole(): UseMutationResult<
  IdentityRole,
  ApiError,
  { id: string; body: IdentityRoleUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation<IdentityRole, ApiError, { id: string; body: IdentityRoleUpdate }>({
    mutationFn: ({ id, body }) => put(rota('/api/v1/identity-roles/{id}', id), { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.papeis });
    },
  });
}

/** Excluir papel sem identidades (FR-118, FR-130). Com identidades → 409 `papel_em_uso`. */
export function useDeleteIdentityRole(): UseMutationResult<unknown, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (id) => del(rota('/api/v1/identity-roles/{id}', id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.papeis });
    },
  });
}

/** Cadastro deliberado de papel em T21 (origem `manual`). */
export function useCreateIdentityRoleT21(): UseMutationResult<
  IdentityRole,
  ApiError,
  { name: string; description: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation<IdentityRole, ApiError, { name: string; description: string | null }>({
    mutationFn: (body) => post('/api/v1/identity-roles', { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.papeis });
    },
  });
}

// --- cadastro de ambientes (Lote 3 · F1-c) ---------------------------------------
//
// O CADASTRO É COMPARTILHADO PELAS TELAS, E A CHAVE DE CACHE É UMA SÓ
//
// Environments, Approval Workflow, T5 (o combobox «Ambiente declarado») e T4 (o filtro)
// leem a MESMA lista. Uma chave por tela faria a criação de um ambiente aparecer no
// card e não no combobox até alguém recarregar — e a pessoa que acabou de criar o
// ambiente é justamente a que vai usá-lo em seguida.

/**
 * O cadastro de ambientes do tenant, exibido em Environments.
 *
 * Traz as DUAS vistas — inclusive as linhas que não são ambiente de identidade
 * (metadado do catálogo do executor). Quem oferece ambiente para uma identidade
 * **filtra por `selectable_for_identity`**: uma linha do eixo C num combobox de T5
 * daria a um agente um ambiente que nunca teve autoridade nenhuma.
 *
 * `useEnvironmentsSelectable` abaixo é o atalho usado pelos seletores de identidade;
 * Approval Workflow lê o cadastro completo e explica os ambientes não selecionáveis.
 */
export function useEnvironments(): UseQueryResult<EnvironmentList, ApiError> {
  return useQuery<EnvironmentList, ApiError>({
    queryKey: tenantKeys.environments,
    queryFn: () => get('/api/v1/tenant/environments'),
  });
}

/**
 * Só os ambientes que uma identidade pode receber, na ordem do cadastro.
 *
 * Existe como hook (e não como `.filter()` em cada tela) porque é uma REGRA e não
 * uma conveniência: o filtro esquecido num só lugar é o vazamento do eixo C, e o
 * sintoma seria um agente `lab` a aprovar a própria mudança de autoridade.
 */
export function useEnvironmentsSelectable(): UseQueryResult<Environment[], ApiError> {
  return useQuery<EnvironmentList, ApiError, Environment[]>({
    queryKey: tenantKeys.environments,
    queryFn: () => get('/api/v1/tenant/environments'),
    select: selectSelectable,
  });
}

/**
 * O `select` vive FORA do hook para o resultado ser estável entre renders.
 *
 * Com um arrow inline, o react-query vê uma função nova a cada render e reexecuta a
 * projeção, devolvendo um array NOVO. Quem depende de `data` num `useMemo` (o
 * drawer de T5 monta as opções e os slugs do schema a partir dele) recomputa
 * sempre, e o resolver do formulário é recriado — com `mode: 'onChange'`, isso
 * revalida a cada tecla.
 *
 * **Sem número que o justifique, e eu tentei um.** Suspeitei que fosse a causa de a
 * suíte do `IdentityDrawer` levar ~150 s e medi as duas versões: 146 s com o arrow
 * inline, 160 s com esta — ou seja, ruído, não melhora. A suíte é lenta por ter 47
 * casos de DOM a 2–5 s cada, e não por isto. A forma fica porque é a correta
 * (resultado estável é o contrato do `select`), não porque acelere algo.
 */
const selectSelectable = (data: EnvironmentList) =>
  data.items.filter((e) => e.selectable_for_identity);

/**
 * Cadastrar um ambiente.
 *
 * Invalida também as identidades: o rótulo que T4 e T5 mostram vem do cadastro, e
 * um ambiente novo muda o que aquelas telas podem exibir.
 */
export function useCreateEnvironment(): UseMutationResult<
  Environment,
  ApiError,
  EnvironmentInput
> {
  const queryClient = useQueryClient();
  return useMutation<Environment, ApiError, EnvironmentInput>({
    mutationFn: (body) => post('/api/v1/tenant/environments', { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.environments });
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.todas });
    },
  });
}

/**
 * Editar um ambiente — **renomear o slug incluído**.
 *
 * O servidor propaga o slug às identidades e aos recursos (`ON UPDATE CASCADE`).
 * Invalidar só o cadastro deixaria T4 a mostrar o slug antigo. As regras de
 * Approval Workflow pertencem ao UUID do ambiente e não mudam com o renome.
 */
export function useUpdateEnvironment(): UseMutationResult<
  Environment,
  ApiError,
  { id: string; body: EnvironmentInput }
> {
  const queryClient = useQueryClient();
  return useMutation<Environment, ApiError, { id: string; body: EnvironmentInput }>({
    mutationFn: ({ id, body }) => put(rota('/api/v1/tenant/environments/{id}', id), { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.environments });
      void queryClient.invalidateQueries({ queryKey: identitiesKeys.todas });
    },
  });
}

/**
 * Excluir um ambiente que ninguém usa.
 *
 * `409 environment_in_use` traz `data.identities`, `data.resources` e — quando o S-d
 * (Fase 2) ligar a contagem — `data.namespaces`. A tela usa as contagens para dizer o
 * que fazer, em vez de repetir «está em uso». O `errors.ts` já classifica o código
 * como `informar` + `gravacao: 'nada'`.
 *
 * `namespaces` é OPCIONAL no tipo gerado e vem AUSENTE quando é zero: «nenhum
 * mapeamento» e «esta versão do servidor não conta mapeamentos» não são a mesma
 * coisa, e quem desenhar a frase deve tratar ausência como a primeira.
 *
 * **A FRASE do catálogo NÃO nomeia mapeamentos, de propósito, até o S-d ligar a
 * contagem.** O campo pode entrar antes porque se esconde sozinho quando é zero; a
 * frase não tem esse mecanismo — ela aparece em TODO `environment_in_use`, e hoje
 * todos são causados por identidade ou recurso. Nomear ali uma funcionalidade que o
 * produto ainda não tem manda o leitor procurar o que não existe.
 *
 * Quando o S-d chegar, a frase entra NO MESMO COMMIT do `select` que a torna
 * verdadeira — e o formato a copiar é o do irmão: o `p1-sites` acabou de partir
 * `site_em_uso` numa FAMÍLIA por causa (`both`/`onlyIdentities`/`onlyResources`), que
 * diz a saída certa em vez de uma disjunção que serve para todos os casos e para
 * nenhum. `environment_in_use` deve seguir o mesmo caminho, com três causas.
 */
export function useDeleteEnvironment(): UseMutationResult<unknown, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (id) => del(rota('/api/v1/tenant/environments/{id}', id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.environments });
    },
  });
}
